import { useEffect, useRef, useState } from 'react'
import { Trans } from '@lingui/react/macro'

import Spinner from '@jotta/ui/Spinner'
import { cn } from '@jotta/utils/css'

import Cloud from './CaptchaCloud.png'
import File from './CaptchaFile.png'
import type { CaptchaSolution } from './CreateUser'

const canvasWidth = 452
const canvasHeight = 257
const fileSize = 64
const cloudSize = 128

function clamp(x: number, y: number, size: number) {
  const halfSize = size / 2
  if (x < halfSize) {
    x = halfSize
  }
  if (y < halfSize) {
    y = halfSize
  }
  if (x > canvasWidth - halfSize) {
    x = canvasWidth - halfSize
  }
  if (y > canvasHeight - halfSize) {
    y = canvasHeight - halfSize
  }
  return {
    x,
    y,
  }
}

function randomPosition(targetSize: number) {
  return clamp(
    Math.random() * canvasWidth,
    Math.random() * canvasHeight,
    targetSize,
  )
}

function distance(x1: number, y1: number, x2: number, y2: number) {
  return Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2)
}

function collision(
  x: number,
  y: number,
  targetX: number,
  targetY: number,
  targetSize: number,
) {
  return (
    Math.abs(targetX - x) < targetSize && Math.abs(targetY - y) < targetSize
  )
}

export function Captcha({
  onCaptchaSolved,
}: {
  onCaptchaSolved: (solution: CaptchaSolution) => void
}) {
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>()
  const [success, setSuccess] = useState(false)
  const cloudRef = useRef<HTMLImageElement>(null)

  const fileRef = useRef<HTMLImageElement>(null)
  useEffect(() => {
    if (!canvas) {
      return
    }
    const context = canvas.getContext('2d')
    if (!context) {
      return
    }
    const start = Date.now()
    let hover = false
    let drag = false
    let { x: fileX, y: fileY } = randomPosition(fileSize)
    let cloudX
    let cloudY
    do {
      const { x, y } = randomPosition(cloudSize)
      cloudX = x
      cloudY = y
    } while (distance(fileX, fileY, cloudX, cloudY) < cloudSize)

    let animationFrameId: number
    const render = () => {
      context.reset()

      const cloud = cloudRef.current
      if (cloud) {
        context.drawImage(
          cloud,
          cloudX - cloudSize / 2,
          cloudY - cloudSize / 2,
          cloudSize,
          cloudSize,
        )
      }

      const file = fileRef.current
      if (file) {
        const size = hover ? fileSize + 2 : fileSize
        context.drawImage(file, fileX - size / 2, fileY - size / 2, size, size)
      }

      animationFrameId = window.requestAnimationFrame(render)
    }
    render()

    const pointerMove = (event: TouchEvent | MouseEvent) => {
      // determine pointer position
      let x: number | undefined = undefined
      let y: number | undefined = undefined
      if (event instanceof TouchEvent && event.touches.length > 0) {
        const target = event.target
        if (target instanceof HTMLCanvasElement) {
          const { clientX, clientY } = event.touches[0]
          x = clientX - target.offsetLeft
          y = clientY - target.offsetTop
        }
      }
      if (event instanceof MouseEvent) {
        const { offsetX, offsetY } = event
        x = offsetX
        y = offsetY
      }

      if (x !== undefined && y !== undefined) {
        // hover
        if (collision(x, y, fileX, fileY, fileSize)) {
          hover = true
          canvas.className = cn('cursor-pointer')
        } else if (!drag) {
          hover = false
          canvas.className = ''
        }

        // drag
        if (drag) {
          const clamped = clamp(x, y, fileSize)
          x = clamped.x
          y = clamped.y

          fileX = x
          fileY = y

          if (collision(x, y, cloudX, cloudY, cloudSize / 4)) {
            setSuccess(prevState => {
              if (!prevState) {
                onCaptchaSolved({
                  solution: `${Date.now()}`,
                  timeMillis: Date.now() - start,
                })
              }
              return true
            })
          }
        }
      }
    }

    const pointerDown = (event: TouchEvent | MouseEvent) => {
      pointerMove(event)
      if (hover) {
        drag = true
      }
    }
    const pointerUp = () => {
      drag = false
    }

    if (canvas) {
      canvas.addEventListener('mousedown', pointerDown)
      canvas.addEventListener('mousemove', pointerMove)
      canvas.addEventListener('mouseup', pointerUp)
      canvas.addEventListener('touchstart', pointerDown)
      canvas.addEventListener('touchmove', pointerMove)
      canvas.addEventListener('touchend', pointerUp)
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId)

      if (canvas) {
        canvas.removeEventListener('mousedown', pointerDown)
        canvas.removeEventListener('mousemove', pointerMove)
        canvas.removeEventListener('mouseup', pointerUp)
        canvas.removeEventListener('touchstart', pointerDown)
        canvas.removeEventListener('touchmove', pointerMove)
        canvas.removeEventListener('touchend', pointerUp)
      }
    }
  }, [canvas, setSuccess, onCaptchaSolved])

  if (success) {
    return <Spinner />
  }

  return (
    <>
      <p>
        <Trans>Drag the file onto the cloud to prove you're not a robot</Trans>
      </p>
      <div className="rounded-lg border border-grey5 bg-grey1">
        <canvas ref={setCanvas} width={canvasWidth} height={canvasHeight} />
      </div>
      <img hidden ref={cloudRef} src={Cloud} alt="" />
      <img hidden ref={fileRef} src={File} alt="" />
    </>
  )
}
